import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer"; // Image imported
import { formatCurrency } from '../utils/currencyHelper'; 
// --- Action Imports ---
import { getExerciseAssignments } from "../../actions/exerciseAssignmentAction";
import { getPatientDiet } from "../../actions/patientDietActions";
import { getAllAppointments } from "../../actions/appointmentActions";
import { getAllDoctors } from "../../actions/doctorActions";
import { imgUrl } from "../../actions/types";
import { getAllPrescriptions } from "../../actions/prescriptionActions";
import { getAllMedicalRecords } from "../../actions/medicalRecordActions";
import { getOnePatientProfile } from "src/actions/patientProfileActions";
import { listAllInvoices } from "../../actions/invoiceActions";
import MessageModal from "../Pages/MessageModel";
import {
  patientGenerateToken,
  clearVideoState,
} from "../../actions/videoActions";

import "../../scss/style.scss";

// --- Helper Functions ---
const calculateBmi = (weight, height) => {
  const weightKg = parseFloat(weight);
  const heightCm = parseFloat(height);
  if (!weightKg || !heightCm || weightKg <= 0 || heightCm <= 0) return "N/A";
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);
  return bmi.toFixed(1);
};

const getBmiStatus = (bmi) => {
  const bmiValue = parseFloat(bmi);
  if (isNaN(bmiValue) || bmiValue <= 0)
    return { status: "N/A", className: "text-muted" };
  if (bmiValue < 18.5) return { status: "Underweight", className: "text-info" };
  if (bmiValue <= 24.9) return { status: "Normal", className: "text-success" };
  if (bmiValue <= 29.9)
    return { status: "Overweight", className: "text-warning" };
  return { status: "Obesity", className: "text-danger" };
};

const getInvoiceBadgeClass = (status) => {
  switch (status) {
    case "Paid":
      return "badge badge-pill paid-label";
    case "Pending":
      return "badge badge-pill pending-label";
    case "Cancelled":
      return "badge badge-pill cancel-label";
    default:
      return "badge badge-pill failed-label";
  }
};
// --- (NEW) Professional PDF Document Components ---

const pdfStyles = StyleSheet.create({
  // General Page & Text Styles
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    padding: "80px 40px 60px 40px",
    color: "#333",
  },
  h2: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: "#007bff",
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 3,
  },
  label: { fontFamily: "Helvetica-Bold", color: "#555" },
  text: { fontSize: 10, lineHeight: 1.4 },

  // --- Reusable Header & Footer ---
  header: {
    position: "absolute",
    top: 25,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: "#007bff",
    paddingBottom: 10,
  },
  headerCol: { width: "33.33%" },
  headerLogo: { width: 60, height: 60, objectFit: "contain" },
  headerTitle: { fontSize: 22, fontFamily: "Helvetica-Bold", color: "#007bff" },
  headerDoctorDetails: { textAlign: "right" },
  doctorName: { fontSize: 14, fontFamily: "Helvetica-Bold" },
  doctorSubtext: { fontSize: 10, color: "#555" },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 8,
    color: "#888",
  },
  footerText: { marginBottom: 2 },
  pageNumber: {
    position: "absolute",
    bottom: 0,
    right: 0,
    fontSize: 8,
    color: "#888",
  },

  // Layout & Sections
  section: { marginBottom: 15 },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: 10,
    marginBottom: 20,
    backgroundColor: "#f8f9fa",
  },
  infoColumn: { flexBasis: "48%" },

  // Table Styles
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 4,
    overflow: "hidden",
  },
  tableRow: { flexDirection: "row" },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  tableCol: {
    padding: 6,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: { fontSize: 9 },
  tableHeaderCell: { fontFamily: "Helvetica-Bold", fontSize: 10 },

  // Signature
  signatureSection: {
    position: "absolute",
    bottom: 80,
    right: 40,
    textAlign: "center",
  },
  signatureImage: { width: 120, height: 40, objectFit: "contain" },
  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: "#333",
    width: 150,
    marginTop: 40,
    marginBottom: 5,
  },
  signatureText: { fontSize: 10, fontFamily: "Helvetica-Bold" },
});

// Reusable Header Component for ALL Reports
const ReportHeader = ({ doctor, title }) => (
  <View style={pdfStyles.header} fixed>
    <View style={pdfStyles.headerCol}>
      {doctor?.ClinicLogo && (
        <Image
          style={pdfStyles.headerLogo}
          src={`${imgUrl}${doctor.ClinicLogo}`}
        />
      )}
    </View>
    <View style={[pdfStyles.headerCol, { alignItems: "center" }]}>
      <Text style={pdfStyles.headerTitle}>{title}</Text>
    </View>
    <View style={[pdfStyles.headerCol, pdfStyles.headerDoctorDetails]}>
      <Text style={pdfStyles.doctorName}>
        Dr. {doctor?.firstName} {doctor?.lastName}
      </Text>
      <Text style={pdfStyles.doctorSubtext}>
        {doctor?.Specializations?.[0]?.Specialization || "Specialist"}
      </Text>
      {/* --- (FIXED) Correctly accessing registration number --- */}
      <Text style={pdfStyles.doctorSubtext}>
        Doc.ID: {doctor?.hospitalID || "N/A"}
      </Text>
    </View>
  </View>
);

// Reusable Footer Component
const ReportFooter = ({ doctor }) => (
  <View style={pdfStyles.footer} fixed>
    <Text style={pdfStyles.footerText}>
      Thank you for choosing {doctor?.ClinicInfoName || "our clinic"}.
    </Text>
    <Text style={pdfStyles.footerText}>
      For any questions, please contact us at {doctor?.phoneNumber || "N/A"}.
    </Text>
    <Text
      style={pdfStyles.pageNumber}
      render={({ pageNumber, totalPages }) =>
        `Page ${pageNumber} / ${totalPages}`
      }
    />
  </View>
);

// 1. Redesigned Prescription PDF
const PrescriptionPDF = ({ prescriptions, patient, doctor, appointmentId }) => {
  const signatureFileName = prescriptions.find((p) => p.signature)?.signature;
  return (
    <Document title={`Prescription - ${patient?.firstName}`}>
      <Page size="A4" style={pdfStyles.page}>
        <ReportHeader doctor={doctor} title="Prescription" />

        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoColumn}>
            <Text>
              <Text style={pdfStyles.label}>Patient:</Text> {patient?.firstName}{" "}
              {patient?.lastName}
            </Text>
            <Text>
              <Text style={pdfStyles.label}>Patient ID:</Text>{" "}
              {patient?.PatientID}
            </Text>
          </View>
          <View style={[pdfStyles.infoColumn, { textAlign: "right" }]}>
            <Text>
              <Text style={pdfStyles.label}>Appointment ID:</Text> #
              {appointmentId}
            </Text>
            <Text>
              <Text style={pdfStyles.label}>Date:</Text>{" "}
              {new Date().toLocaleDateString()}
            </Text>
          </View>
        </View>

        <View style={pdfStyles.section}>
          <Text style={pdfStyles.h2}>Medications (Rx)</Text>
          <View style={pdfStyles.table}>
            <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
              <View style={[pdfStyles.tableCol, { width: "35%" }]}>
                <Text style={pdfStyles.tableHeaderCell}>Medicine</Text>
              </View>
              <View style={[pdfStyles.tableCol, { width: "20%" }]}>
                <Text style={pdfStyles.tableHeaderCell}>Frequency</Text>
              </View>
              <View style={[pdfStyles.tableCol, { width: "20%" }]}>
                <Text style={pdfStyles.tableHeaderCell}>Duration</Text>
              </View>
              <View style={[pdfStyles.tableCol, { width: "25%" }]}>
                <Text style={pdfStyles.tableHeaderCell}>Timing</Text>
              </View>
            </View>
            {prescriptions.map((p, i) => (
              <View
                style={[
                  pdfStyles.tableRow,
                  i % 2 !== 0 && { backgroundColor: "#fafafa" },
                ]}
                key={i}
              >
                <View style={[pdfStyles.tableCol, { width: "35%" }]}>
                  <Text style={pdfStyles.tableCell}>{p.name}</Text>
                </View>
                <View style={[pdfStyles.tableCol, { width: "20%" }]}>
                  <Text style={pdfStyles.tableCell}>{p.quantity}</Text>
                </View>
                <View style={[pdfStyles.tableCol, { width: "20%" }]}>
                  <Text style={pdfStyles.tableCell}>{p.days}</Text>
                </View>
                <View style={[pdfStyles.tableCol, { width: "25%" }]}>
                  <Text style={pdfStyles.tableCell}>
                    {Array.isArray(p.time) ? p.time.join(", ") : p.time}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {signatureFileName && (
          <View style={pdfStyles.signatureSection}>
            <Image
              style={pdfStyles.signatureImage}
              src={`${imgUrl}${signatureFileName}`}
            />
            <View style={pdfStyles.signatureLine} />
            <Text style={pdfStyles.signatureText}>
              Dr. {doctor?.firstName} {doctor?.lastName}
            </Text>
          </View>
        )}

        <ReportFooter doctor={doctor} />
      </Page>
    </Document>
  );
};
// --- EXERCISE CHART PDF TEMPLATE ---
const ExerciseChartPDF = ({ exercises, patient, doctor, appointmentId }) => (
  <Document title={`Exercise Chart - ${patient?.firstName}`}>
    <Page size="A4" style={pdfStyles.page}>
      <ReportHeader doctor={doctor} title="Exercise Chart" />
      
      {/* 1. Patient Info Header */}
      <View style={pdfStyles.infoSection}>
        <View style={pdfStyles.infoColumn}>
          <Text><Text style={pdfStyles.label}>Patient:</Text> {patient?.firstName} {patient?.lastName}</Text>
          <Text><Text style={pdfStyles.label}>Patient ID:</Text> {patient?.PatientID}</Text>
        </View>
        <View style={[pdfStyles.infoColumn, { textAlign: "right" }]}>
          <Text><Text style={pdfStyles.label}>Appt ID:</Text> #{appointmentId}</Text>
          <Text><Text style={pdfStyles.label}>Issued Date:</Text> {new Date().toLocaleDateString()}</Text>
        </View>
      </View>

      <Text style={pdfStyles.h2}>Physiotherapy / Exercise Instructions</Text>

      {/* 2. Loop Through Assignments */}
      {exercises.map((asgn) => (
        <View key={asgn.id} style={{ marginBottom: 15 }}>
          {asgn.assigned_exercises?.map((ex, exIdx) => (
            <View key={exIdx} style={{ marginBottom: 15, border: "1px solid #eee", padding: 10 }} wrap={false}>
              
              {/* Exercise Title and Frequency Header */}
              <View style={{ backgroundColor: "#f0f8ff", padding: 5, flexDirection: "row", justifyContent: "space-between" }}>
                <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 10 }}>{ex.exercise_title}</Text>
                <Text style={{ fontSize: 8, fontFamily: "Helvetica-Bold" }}>{asgn.frequency} - {asgn.time_of_day}</Text>
              </View>

              {/* --- NAYA: VALIDITY DATE SECTION --- */}
              <View style={{ padding: 5, borderBottomWidth: 0.5, borderBottomColor: "#ddd", marginBottom: 5, backgroundColor: "#fafafa" }}>
                <Text style={{ fontSize: 8, color: "#d9534f", fontFamily: "Helvetica-Bold" }}>
                  Plan Period: {asgn.start_date} to {asgn.end_date || "Continuous/Next Advice"}
                </Text>
              </View>

              <Text style={{ fontSize: 8, color: "#666", marginTop: 4, italic: true }}>Instructions: {ex.description}</Text>

              {/* 3. Steps with Images */}
              {ex.steps?.map((step, sIdx) => (
                <View key={sIdx} style={{ flexDirection: "row", marginTop: 8, borderBottomWidth: 0.5, borderBottomColor: "#eee", paddingBottom: 5 }}>
                  
                  {/* 40% Image Area */}
                  <View style={{ width: "30%", marginRight: 10 }}>
                    {step.image && step.image !== "null" ? (
                      <Image 
                        style={{ width: "100%", height: 70, objectFit: "contain", borderRadius: 4 }} 
                        src={`${imgUrl}${step.image}`} 
                      />
                    ) : (
                      <View style={{ width: "100%", height: 70, backgroundColor: "#f9f9f9" }} />
                    )}
                  </View>

                  {/* 60% Text Area */}
                  <View style={{ width: "70%" }}>
                    <Text style={{ fontSize: 9, fontFamily: "Helvetica-Bold", color: "#007bff" }}>
                      Step {step.step_number}: {step.step_title}
                    </Text>
                    <Text style={{ fontSize: 8, color: "#333", marginTop: 3 }}>
                      {step.description}
                    </Text>
                    {step.duration_seconds > 0 && (
                      <Text style={{ fontSize: 7, color: "#666", marginTop: 4 }}>
                        Duration: {step.duration_seconds} Seconds
                      </Text>
                    )}
                  </View>

                </View>
              ))}
            </View>
          ))}
        </View>
      ))}

      <ReportFooter doctor={doctor} />
    </Page>
  </Document>
);


// --- DIET CHART PDF TEMPLATE (UPDATED) ---
const DietChartPDF = ({ diets, patient, doctor, appointmentId }) => (
  <Document title={`Diet Chart - ${patient?.firstName}`}>
    <Page size="A4" style={pdfStyles.page}>
      <ReportHeader doctor={doctor} title="Diet Plan" />
      <View style={pdfStyles.infoSection}>
        <View style={pdfStyles.infoColumn}>
          <Text><Text style={pdfStyles.label}>Patient:</Text> {patient?.firstName} {patient?.lastName}</Text>
          <Text><Text style={pdfStyles.label}>Patient ID:</Text> {patient?.PatientID}</Text>
        </View>
        <View style={[pdfStyles.infoColumn, { textAlign: "right" }]}>
          <Text><Text style={pdfStyles.label}>Appt ID:</Text> #{appointmentId}</Text>
          <Text><Text style={pdfStyles.label}>Date:</Text> {new Date().toLocaleDateString()}</Text>
        </View>
      </View>

      {/* diets = array of assignments */}
      {diets.map((asgn) => (
        <View key={asgn.id} style={{ marginBottom: 15 }}>
          {/* Loop through each diet plan inside assigned_diets array */}
          {asgn.assigned_diets?.map((plan, pIdx) => (
            <View key={pIdx} style={{ marginBottom: 15 }}>
              <View style={{ backgroundColor: "#fff4e6", padding: 8, borderLeft: "4px solid #ff8c00", marginBottom: 5 }}>
                <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 11, color: "#d97706" }}>
                  Plan: {plan.title} ({plan.diet_type || 'General'})
                </Text>
                <Text style={{ fontSize: 8 }}>Validity: {asgn.start_date} to {asgn.end_date || 'Next Advice'}</Text>
              </View>

              <View style={pdfStyles.table}>
                <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
                  <View style={[pdfStyles.tableCol, { width: "30%" }]}><Text style={pdfStyles.tableHeaderCell}>Meal</Text></View>
                  <View style={[pdfStyles.tableCol, { width: "70%" }]}><Text style={pdfStyles.tableHeaderCell}>Details & Instructions</Text></View>
                </View>
                {plan.meals?.map((meal, mIdx) => (
                  <View key={mIdx} style={pdfStyles.tableRow}>
                    <View style={[pdfStyles.tableCol, { width: "30%" }]}><Text style={pdfStyles.tableCell}>{meal.meal_type}</Text></View>
                    <View style={[pdfStyles.tableCol, { width: "70%" }]}>
                      <Text style={[pdfStyles.tableCell, { fontFamily: "Helvetica-Bold" }]}>{meal.food_items}</Text>
                      <Text style={{ fontSize: 8, color: "#777", marginTop: 2 }}>{meal.instructions}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </View>
      ))}
      <ReportFooter doctor={doctor} />
    </Page>
  </Document>
);
// --- (FIXED) Redesigned Medical Record PDF ---
// It now correctly accepts an array of 'records' and maps over them.
const MedicalRecordPDF = ({ records, patient, doctor }) => (
  <Document title={`Medical Record - ${patient?.firstName}`}>
    <Page size="A4" style={pdfStyles.page}>
      <ReportHeader doctor={doctor} title="Medical Record" />

      <View style={pdfStyles.infoSection}>
        <View style={pdfStyles.infoColumn}>
          <Text>
            <Text style={pdfStyles.label}>Patient:</Text> {patient?.firstName}{" "}
            {patient?.lastName}
          </Text>
          <Text>
            <Text style={pdfStyles.label}>Patient ID:</Text>{" "}
            {patient?.PatientID}
          </Text>
        </View>
        {/* Display general date if needed, or specific record date inside map */}
      </View>

      {/* Loop through each record in the 'records' array */}
      {records &&
        records.map((record, index) => (
          <View style={pdfStyles.section} key={record.id || index} wrap={false}>
            {/* Add a separator if there are multiple records */}
            {index > 0 && (
              <View
                style={{
                  borderBottomWidth: 1,
                  borderBottomColor: "#e0e0e0",
                  margin: "20px 0",
                }}
              />
            )}

            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              <Text style={pdfStyles.h2}>Record Details</Text>
              <Text style={{ fontSize: 10, color: "#555" }}>
                <Text style={pdfStyles.label}>Record ID:</Text> #{record.id} |{" "}
                <Text style={pdfStyles.label}>Date:</Text>{" "}
                {new Date(record.date).toLocaleDateString()}
              </Text>
            </View>

            <Text style={[pdfStyles.h2, { marginTop: 10 }]}>Vitals</Text>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>BP:</Text> {record?.BP || "N/A"}
              </Text>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>Heart Rate:</Text>{" "}
                {record?.heartRate || "N/A"} BPM
              </Text>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>SPO2:</Text>{" "}
                {record?.SPO || "N/A"} %
              </Text>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>Blood Sugar:</Text>{" "}
                {record?.sugar || "N/A"}
              </Text>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>Height:</Text>{" "}
                {record?.height ? `${record.height} cm` : "N/A"}
              </Text>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>Weight:</Text>{" "}
                {record?.weight ? `${record.weight} kg` : "N/A"}
              </Text>
              <Text style={{ width: "48%" }}>
                <Text style={pdfStyles.label}>BMI:</Text>{" "}
                {calculateBmi(record?.weight, record?.height)}
              </Text>
            </View>

            <Text style={[pdfStyles.h2, { marginTop: 20 }]}>
              Clinical Notes
            </Text>
            <Text style={pdfStyles.label}>Diagnosis:</Text>
            <Text style={[pdfStyles.text, { marginBottom: 10 }]}>
              {record?.diagnosis || "N/A"}
            </Text>
            <Text style={pdfStyles.label}>Treatment:</Text>
            <Text style={pdfStyles.text}>{record?.treatment || "N/A"}</Text>

            {record?.uploadFile && (
              <>
                <Text style={[pdfStyles.h2, { marginTop: 20 }]}>
                  Attachment
                </Text>
                <Text style={pdfStyles.text}>File: {record.uploadFile}</Text>
              </>
            )}
          </View>
        ))}

      {doctor?.signature && (
        <View style={pdfStyles.signatureSection}>
          <Image
            style={pdfStyles.signatureImage}
            src={`${imgUrl}${doctor.signature}`}
          />
          <View style={pdfStyles.signatureLine} />
          <Text style={pdfStyles.signatureText}>
            Dr. {doctor?.firstName} {doctor?.lastName}
          </Text>
        </View>
      )}

      <ReportFooter doctor={doctor} />
    </Page>
  </Document>
);
// 3. Redesigned Invoice PDF
const InvoicePDF = ({ invoice, patient, doctor,selectedCurrency  }) => (
  <Document title={`Invoice - ${patient?.firstName}`}>
    <Page size="A4" style={pdfStyles.page}>
      <ReportHeader doctor={doctor} title="Invoice" />

      <View style={pdfStyles.infoSection}>
        <View style={pdfStyles.infoColumn}>
          <Text>
            <Text style={pdfStyles.label}>Billed To:</Text> {patient?.firstName}{" "}
            {patient?.lastName}
          </Text>
          <Text>
            <Text style={pdfStyles.label}>Patient ID:</Text>{" "}
            {patient?.PatientID}
          </Text>
        </View>
        <View style={[pdfStyles.infoColumn, { textAlign: "right" }]}>
          <Text>
            <Text style={pdfStyles.label}>Invoice ID:</Text> INV-{invoice?.id}
          </Text>
          <Text>
            <Text style={pdfStyles.label}>Date Issued:</Text>{" "}
            {new Date(invoice?.date).toLocaleDateString()}
          </Text>
          <Text>
            <Text style={pdfStyles.label}>Status:</Text> {invoice?.Status}
          </Text>
        </View>
      </View>

      <View style={pdfStyles.section}>
        <Text style={pdfStyles.h2}>Payment Summary</Text>
        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <View style={[pdfStyles.tableCol, { width: "75%" }]}>
              <Text style={pdfStyles.tableHeaderCell}>Description</Text>
            </View>
            <View
              style={[pdfStyles.tableCol, { width: "25%", textAlign: "right" }]}
            >
              <Text style={pdfStyles.tableHeaderCell}>Amount</Text>
            </View>
          </View>
          <View style={pdfStyles.tableRow}>
            <View style={[pdfStyles.tableCol, { width: "75%" }]}>
              <Text style={pdfStyles.tableCell}>
                Doctor Consultation Fee for Appointment #
                {invoice?.appointmentId}
              </Text>
            </View>
            <View
              style={[pdfStyles.tableCol, { width: "25%", textAlign: "right" }]}
            >
              <Text style={pdfStyles.tableCell}>{formatCurrency(invoice?.TotalAmount, selectedCurrency)}</Text>
            </View>
          </View>
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          marginTop: 20,
        }}
      >
        <View style={{ width: "40%" }}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              borderBottomWidth: 1,
              borderColor: "#eee",
              paddingBottom: 5,
              marginBottom: 5,
            }}
          >
            <Text>Subtotal:</Text>
            <Text>{formatCurrency(invoice?.TotalAmount, selectedCurrency)}</Text>
          </View>
          <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <Text style={pdfStyles.label}>Total Amount:</Text>
           <Text style={pdfStyles.label}>{formatCurrency(invoice?.TotalAmount, selectedCurrency)}</Text>
          </View>
        </View>
      </View>

      <ReportFooter doctor={doctor} />
    </Page>
  </Document>
);

// Add this helper function to check if current time is within appointment slot
const isWithinAppointmentTime = (appointmentDate, appointmentTime) => {
  const now = new Date();
  const appointmentDateTime = new Date(appointmentDate);

  // Parse the time string (format like "10:30 AM")
  const [time, modifier] = appointmentTime.split(" ");
  let [hours, minutes] = time.split(":");

  hours = parseInt(hours);
  minutes = parseInt(minutes);

  // Convert to 24-hour format
  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  // Set the time on the appointment date
  appointmentDateTime.setHours(hours, minutes, 0, 0);

  // Calculate start and end times (15 minutes before and 1 hour after)
  const startTime = new Date(appointmentDateTime.getTime() - 15 * 60 * 1000); // 15 mins before
  const endTime = new Date(appointmentDateTime.getTime() + 60 * 60 * 1000); // 1 hour after

  return now >= startTime && now <= endTime;
};

const PatientDashboardMain = ({
  appointment,
  appointmentLoading,
  getAllAppointments,
  doctors,
  doctorsLoading,
  getAllDoctors,
  prescription,
  prescriptionLoading,
  getAllPrescriptions,
  medicalRecords,
  medicalRecordsLoading,
  getAllMedicalRecords,
  invoices,
   assignmentList,    // <-- ADD THIS
  patientDietList,   // <-- ADD THIS
  getExerciseAssignments, // Add this too
  getPatientDiet,         // Add this too
  invoicesLoading,
  listAllInvoices,
  patientProfile,
  getOnePatientProfile,
  video,
  patientGenerateToken,
  clearVideoState,
  selectedCurrency,
  history, // <-- ADD these props
}) => {
  const patientId = localStorage.getItem("hospitalCode");
  const [activeTab, setActiveTab] = useState("appointments");
  const [viewingAppointmentDetails, setViewingAppointmentDetails] =
    useState(null);
    const [showExercisePdfModal, setShowExercisePdfModal] = useState(false);
const [showDietPdfModal, setShowDietPdfModal] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showAppointmentPdfModal, setShowAppointmentPdfModal] = useState(false);
  const [showInvoicePdfModal, setShowInvoicePdfModal] = useState(false);
  const [showPrescriptionPdfModal, setShowPrescriptionPdfModal] =
    useState(false);
  const [showMedicalRecordPdfModal, setShowMedicalRecordPdfModal] =
    useState(false);
  const initialFilters = { doctor: "", status: "", startDate: "", endDate: "" };
  const [filters, setFilters] = useState(initialFilters);
  // --- [NEW] State for handling video call joining and errors ---
  const [isJoining, setIsJoining] = useState(null); // Will hold the ID of the appointment being joined
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalVariant, setModalVariant] = useState("info");
  const { token, loading: videoLoading, error: videoError, userRole } = video;

  const showMessageModal = (title, message, variant = "info") => {
    setModalTitle(title);
    setModalMessage(message);
    setModalVariant(variant);
    setModalShow(true);
  };
  useEffect(() => {
    if (patientId) {
      getOnePatientProfile(patientId);
      getExerciseAssignments({ PatientID: patientId }); // Add this
    getPatientDiet({ PatientID: patientId });       
    }
    getAllAppointments();
    getAllDoctors();
    getAllPrescriptions();
    getAllMedicalRecords();
    listAllInvoices();
  }, [
    getAllAppointments,
    getAllDoctors,
    getAllPrescriptions,
    getAllMedicalRecords,
    getOnePatientProfile,
    listAllInvoices,
    patientId,
  ]);

  useEffect(() => {
    console.log("Video state changed:", {
      token,
      videoError,
      userRole,
      isJoining,
    });

    if (token && isJoining) {
      console.log(
        "Token received, navigating to video call. User role:",
        userRole,
      );

      // Add a small delay to ensure the video call component is ready
      setTimeout(() => {
        // Check user role to determine where to navigate
        if (userRole === "doctor") {
          // Doctor goes to the doctor video page
          history.push(`/videopage/${isJoining}`);
        } else if (userRole === "patient") {
          // Patient goes to the patient video page - FIXED PATH
          history.push(`/patient-videocall/${isJoining}`);
        }
      }, 500); // 500ms delay to ensure proper initialization
    }
    if (videoError) {
      showMessageModal("Could Not Join Call", videoError, "danger");
      setIsJoining(null);
      // clearVideoState();
    }
  }, [token, videoError, history, clearVideoState, isJoining, userRole]);

  useEffect(() => {
    const isModalOpen =
      viewingAppointmentDetails ||
      viewingInvoice ||
      showAppointmentPdfModal ||
      showInvoicePdfModal ||
      showPrescriptionPdfModal ||
      showMedicalRecordPdfModal;
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [
    viewingAppointmentDetails,
    viewingInvoice,
    showAppointmentPdfModal,
    showInvoicePdfModal,
    showPrescriptionPdfModal,
    showMedicalRecordPdfModal,
  ]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => setFilters(initialFilters);

  const doctorMap = useMemo(
    () =>
      (doctors || []).reduce((acc, doc) => {
        acc[doc.hospitalID] = doc;
        return acc;
      }, {}),
    [doctors],
  );

  const groupedAndFilteredData = useMemo(() => {
    const patientAppointments = (appointment || []).filter(
      (apt) => String(apt.PatientID) === patientId,
    );

    const invoiceMap = (invoices || []).reduce((acc, inv) => {
      acc[String(inv.appointmentId)] = inv;
      return acc;
    }, {});

    const prescriptionsByAppointment = (prescription || []).reduce(
      (acc, pres) => {
        const id = String(pres.appointmentId);
        if (!acc[id]) acc[id] = [];
        acc[id].push(pres);
        return acc;
      },
      {},
    );

    const medicalRecordsByAppointment = (medicalRecords || []).reduce(
      (acc, rec) => {
        const id = String(rec.appointmentId);
        if (!acc[id]) acc[id] = [];
        acc[id].push(rec);
        return acc;
      },
      {},
    );

    let combinedData = patientAppointments.map((apt) => ({
      ...apt,
      prescriptions:
        prescriptionsByAppointment[String(apt.appointmentId)] || [],
      medicalRecords:
        medicalRecordsByAppointment[String(apt.appointmentId)] || [],
      invoice: invoiceMap[String(apt.appointmentId)] || null,
       exercises: (assignmentList || []).filter(asgn => String(asgn.appointment_id) === String(apt.appointmentId)),
    diets: (patientDietList || []).filter(d => String(d.Appoinment_Id) === String(apt.appointmentId)),
    }));

    const { doctor, status, startDate, endDate } = filters;
    if (doctor || status || startDate || endDate) {
      combinedData = combinedData.filter((item) => {
        const appointmentDate = new Date(item.appointmentDate);
        const meetsDoctor =
          !doctor || String(item.hospitalID) === String(doctor);
        const meetsStatus = !status || item.status === status;
        const meetsStartDate =
          !startDate || appointmentDate >= new Date(startDate);
        const meetsEndDate = !endDate || appointmentDate <= new Date(endDate);
        return meetsDoctor && meetsStatus && meetsStartDate && meetsEndDate;
      });
    }
    combinedData.sort(
      (a, b) => Number(b.appointmentId) - Number(a.appointmentId),
    );

    return combinedData;
  }, [appointment, prescription, medicalRecords, invoices, patientId, filters,assignmentList, patientDietList]);

  const patientInvoices = useMemo(() => {
    return (invoices || [])
      .filter((inv) => String(inv.PatientID) === patientId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [invoices, patientId]);

  const relevantDoctors = useMemo(() => {
    const doctorIds = new Set(
      (appointment || [])
        .filter((a) => String(a.PatientID) === patientId)
        .map((a) => a.hospitalID),
    );
    return (doctors || []).filter((doc) => doctorIds.has(doc.hospitalID));
  }, [doctors, appointment, patientId]);

  const isPageLoading =
    appointmentLoading ||
    doctorsLoading ||
    prescriptionLoading ||
    medicalRecordsLoading ||
    invoicesLoading;
  if (isPageLoading && !patientProfile) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <div
          className="spinner-border text-primary"
          style={{ width: "3rem", height: "3rem" }}
          role="status"
        ></div>
      </div>
    );
  }

  const showSuccessModal = (title, message) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalVariant("success");
    setModalShow(true);
  };
  const showErrorModal = (title, message) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalVariant("danger");
    setModalShow(true);
  };

  // Update your handleJoinVideo function
  const handleJoinVideo = (appointmentId) => {
    setIsJoining(appointmentId);

    // const currentUserId = localStorage.getItem('hospitalCode');
    // const existingToken = localStorage.getItem(`twilioToken_${appointmentId}`);
    // const existingRoomName = localStorage.getItem(`twilioRoom_${appointmentId}`);
    // if (!currentUserId) {
    //     showErrorModal("Authentication Error", "Could not identify user.");
    //     setIsJoining(null);
    //     return;
    // }
    // // If we have both token and room name, redirect directly
    // if (existingToken && existingRoomName) {
    //     console.log("Found existing token and room for appointment", appointmentId);
    //     console.log("Room:", existingRoomName);
    //     console.log("Token available, redirecting directly to video page");

    //     // Store the token and room name for the VideoCall component to use
    //     localStorage.setItem('twilioToken', existingToken);
    //     localStorage.setItem('twilioRoomName', existingRoomName);

    //     // Navigate directly to the video page
    //     history.push(`/videopage/${existingRoomName}`);
    //     return;
    // }

    const payload = {
      appointmentId: appointmentId,
    };

    // For patients, use patientGeneratetoken
    patientGenerateToken(payload);
  };
  return (
    <>
      <div className="page-header filter-bar rounded ">
        <div className="row align-items-center">
          <div className="col">
            <h4 className="page-title">Patient Dashboard</h4>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/doctor/dashboard">Patient Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">Patient Dashboard</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="row no-print">
        <div className="col-md-12">
          <div className="card">
            <nav className="user-tabs mb-4">
              <ul className="filter-bar nav nav-tabs nav-tabs-bottom nav-justified pb-0 ">
                <li className="nav-item">
                  <a
                    className={`nav-link h4 ${
                      activeTab === "appointments" ? "active" : ""
                    }`}
                    href="#pat_appointments"
                    data-toggle="tab"
                    onClick={() => setActiveTab("appointments")}
                  >
                    Appointments & Records
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link h4 ${
                      activeTab === "billing" ? "active" : ""
                    }`}
                    href="#pat_billing"
                    data-toggle="tab"
                    onClick={() => setActiveTab("billing")}
                  >
                    Billing
                  </a>
                </li>
              </ul>
            </nav>
            <div className="card-body pt-0">
              <div className="tab-content pt-0">
                <div
                  className={`tab-pane fade ${
                    activeTab === "appointments" ? "show active" : ""
                  }`}
                  id="pat_appointments"
                >
                  <div className="card-body p-0 mb-3">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Doctor</label>
                          <select
                            name="doctor"
                            value={filters.doctor}
                            onChange={handleFilterChange}
                            className="form-control"
                          >
                            <option value="">All Doctors</option>
                            {relevantDoctors.map((doc) => (
                              <option
                                key={doc.hospitalID}
                                value={doc.hospitalID}
                              >
                                Dr. {doc.firstName} {doc.lastName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label>Status</label>
                          <select
                            name="status"
                            value={filters.status}
                            onChange={handleFilterChange}
                            className="form-control"
                          >
                            <option value="">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Confirm">Confirm</option>
                            <option value="Complete">Complete</option>
                            <option value="Cancel">Cancel</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>From Date</label>
                          <input
                            type="date"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>To Date</label>
                          <input
                            type="date"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-1 d-flex align-items-end">
                        <div className="form-group">
                          {/* <button
                            className="clear-button"
                            onClick={clearFilters}
                          >
                            Clear
                          </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card card-table mb-0">
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-hover table-center mb-0">
                          <thead>
                            <tr>
                              <th>Appt.ID</th>
                              <th>Doctor</th>
                              <th>Appointment Date & Time</th>
                              <th>Type</th>
                              <th>Booked For</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Payment</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedAndFilteredData.length > 0 ? (
                              groupedAndFilteredData.map((item) => {
                                const doctor = doctorMap[item.hospitalID];
                                const paymentStatus = item.invoice
                                  ? item.invoice.Status
                                  : "Not Billed";
                                const paymentBadgeClass = item.invoice
                                  ? getInvoiceBadgeClass(item.invoice.Status)
                                  : "bg-light text-muted";
                                const isJoiningThisCall =
                                  isJoining === item.appointmentId;
                                const isSelfAppointment =
                                  (patientProfile?.firstName || "N/A") +
                                    " " +
                                    (patientProfile?.lastName || "") ===
                                  item.patient_name;

                                return (
                                  <tr
                                    key={item._id || item.appointmentId}
                                    className={
                                      item.status === "Cancel"
                                        ? "table-secondary"
                                        : ""
                                    }
                                  >
                                    {/* Appointment ID */}
                                    <td>
                                      <span className="text-bold">
                                        #{item.appointmentId}
                                      </span>
                                    </td>

                                    {/* Doctor Information */}
                                    <td>
                                      <div
                                        className="d-flex align-items-center"
                                        style={{
                                          cursor: "pointer",
                                          minWidth: "200px",
                                        }}
                                        onClick={() =>
                                          history.push(
                                            `/Doctorprofile/${doctor?.hospitalID}`,
                                            "_blank",
                                          )
                                        }
                                      >
                                        <div className="avatar avatar-sm mr-3">
                                          <img
                                            className="avatar-img rounded-circle"
                                            src={
                                              doctor?.uploadPhoto
                                                ? `${imgUrl}${doctor.uploadPhoto}`
                                                : "/assets/img/doctors/doctor-thumb-01.jpg"
                                            }
                                            alt={`Dr. ${doctor?.firstName} ${doctor?.lastName}`}
                                            style={{
                                              width: "40px",
                                              height: "40px",
                                              objectFit: "cover",
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <h6 className="mb-0 text-dark">
                                            Dr. {doctor?.firstName}{" "}
                                            {doctor?.lastName}
                                          </h6>
                                          <small className="text-muted">
                                            {doctor?.specialization || ""}
                                          </small>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Appointment Date & Time */}
                                    <td>
                                      <div className="d-flex flex-column">
                                        <span className="font-weight-semibold text-dark">
                                          {new Date(
                                            item.appointmentDate,
                                          ).toLocaleDateString("en-US", {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          })}
                                        </span>
                                        <span className="text-info font-weight-bold">
                                          {item.appointmentTime}
                                        </span>
                                        {item.duration && (
                                          <small className="text-muted">
                                            {item.duration} min consultation
                                          </small>
                                        )}
                                      </div>
                                    </td>

                                    {/* Appointment Type */}
                                    <td>
                                      <span
                                        className={`badge badge-pill ${
                                          item.type === "video"
                                            ? "bg-primary-light text-info"
                                            : item.type === "home"
                                              ? "bg-success-light text-success"
                                              : "bg-info-light text-info"
                                        }`}
                                      >
                                        <i
                                          className={`fas ${
                                            item.type === "video"
                                              ? "fa-video"
                                              : item.type === "home"
                                                ? "fa-home"
                                                : "fa-user-md"
                                          } mr-1`}
                                        ></i>

                                        <span className="text-capitalize">
                                          {item.type}
                                        </span>
                                      </span>
                                    </td>
                                    {/*  Patient Relation */}
                                    <td>
                                      <div className="d-flex flex-column">
                                        <small className="text-dark font-weight-bold mt-1">
                                          <i className="fas fa-user-circle mr-1"></i>
                                          {item.patient_name}
                                        </small>

                                        {(
                                          (patientProfile?.firstName || "") +
                                          " " +
                                          (patientProfile?.lastName || "")
                                        )
                                          .toLowerCase()
                                          .trim() ===
                                        (item.patient_name || "")
                                          .toLowerCase()
                                          .trim() ? (
                                          <small
                                            className="text-success"
                                            style={{
                                              fontSize: "11px",
                                              fontWeight: "500",
                                            }}
                                          >
                                            <i className="fas fa-user-check mr-1"></i>
                                            (Self)
                                          </small>
                                        ) : (
                                          <small
                                            className="text-info"
                                            style={{
                                              fontSize: "11px",
                                              fontWeight: "500",
                                            }}
                                          >
                                            <i className="fas fa-users mr-1"></i>
                                            (Family Member)
                                          </small>
                                        )}
                                      </div>
                                    </td>

                                    {/* Amount */}
                                    <td>
                                       <span className="font-weight-semibold text-dark">
    {item.paidAmount && item.paidAmount !== "0"
      ? formatCurrency(item.paidAmount, selectedCurrency)
      : item.invoice
        ? formatCurrency(item.invoice.TotalAmount, selectedCurrency)
        : "—"}
  </span>
                                    </td>

                                    {/* Appointment Status */}
                                    <td>
                                      <span
                                        className={`badge badge-pill ${
                                          item.status === "Pending"
                                            ? "pending-label"
                                            : item.status === "Complete"
                                              ? "complete-label"
                                              : item.status === "Cancel"
                                                ? "cancel-label"
                                                : item.status === "Failed"
                                                  ? "failed-label"
                                                  : "confirm-label"
                                        }`}
                                      >
                                        {item.status === "Pending"
                                          ? "Scheduled"
                                          : item.status}
                                      </span>
                                    </td>

                                    {/* Payment Status */}
                                    <td>
                                      <span
                                        className={`badge badge-pill ${
                                          paymentStatus === "Paid"
                                            ? "paid-label"
                                            : paymentStatus === "Pending"
                                              ? "pending-label"
                                              : "not-billed-label"
                                        }`}
                                      >
                                        {paymentStatus === "Paid"
                                          ? "Paid"
                                          : paymentStatus === "Pending"
                                            ? "Pending"
                                            : "Not Billed"}
                                      </span>
                                    </td>

                                    {/* Actions */}
                                    <td className="text-right">
                                      <div className="table-action">
                                        {/* Video Call Status & Join Button */}
                                        {item.type === "video" &&
                                          (() => {
                                            const now = new Date();
                                            const appointmentDate = new Date(
                                              item.appointmentDate,
                                            );
                                            const [time, modifier] =
                                              item.appointmentTime.split(" ");
                                            let [hours, minutes] =
                                              time.split(":");

                                            hours = parseInt(hours);
                                            minutes = parseInt(minutes || 0);

                                            // Convert to 24-hour format
                                            if (modifier === "PM" && hours < 12)
                                              hours += 12;
                                            if (
                                              modifier === "AM" &&
                                              hours === 12
                                            )
                                              hours = 0;

                                            // Set the time on the appointment date
                                            appointmentDate.setHours(
                                              hours,
                                              minutes,
                                              0,
                                              0,
                                            );

                                            // Calculate time differences
                                            const timeUntilAppointment =
                                              appointmentDate - now;
                                            const timeSinceAppointment =
                                              now - appointmentDate;
                                            const fifteenMinutes =
                                              15 * 60 * 1000;
                                            const oneHour = 60 * 60 * 1000;
                                            const appointmentEndTime = new Date(
                                              appointmentDate.getTime() +
                                                (item.duration || 60) *
                                                  60 *
                                                  1000,
                                            );

                                            // Status determination logic
                                            if (item.status === "Cancel") {
                                              return (
                                                <span className="badge badge-pill bg-light text-muted border mr-2">
                                                  <i className="fas fa-ban text-danger mr-1"></i>
                                                  Cancelled
                                                </span>
                                              );
                                            } else if (
                                              item.status === "Complete"
                                            ) {
                                              return (
                                                <span className="badge badge-pill bg-light text-muted border mr-2">
                                                  <i className="fas fa-check-circle text-success mr-1"></i>
                                                  Completed
                                                </span>
                                              );
                                            } else if (
                                              timeUntilAppointment >
                                              24 * 60 * 60 * 1000
                                            ) {
                                              // More than 24 hours before appointment
                                              const daysUntil = Math.floor(
                                                timeUntilAppointment /
                                                  (24 * 60 * 60 * 1000),
                                              );
                                              return (
                                                <span className="badge badge-pill bg-light text-secondary border mr-2">
                                                  <i className="fas fa-calendar text-secondary mr-1"></i>
                                                  In {daysUntil}d
                                                </span>
                                              );
                                            } else if (
                                              timeUntilAppointment > oneHour
                                            ) {
                                              // 1 hour to 24 hours before appointment
                                              const hoursUntil = Math.floor(
                                                timeUntilAppointment /
                                                  (60 * 60 * 1000),
                                              );
                                              return (
                                                <span className="badge badge-pill bg-warning-light text-warning border mr-2">
                                                  <i className="fas fa-clock text-warning mr-1"></i>
                                                  In {hoursUntil}h
                                                </span>
                                              );
                                            } else if (
                                              timeUntilAppointment >
                                              fifteenMinutes
                                            ) {
                                              // 15 minutes to 1 hour before appointment
                                              const minsUntil = Math.floor(
                                                timeUntilAppointment /
                                                  (60 * 1000),
                                              );
                                              return (
                                                <span className="badge badge-pill bg-warning text-white border-0 mr-2">
                                                  <i className="fas fa-exclamation-circle mr-1"></i>
                                                  In {minsUntil}m
                                                </span>
                                              );
                                            } else if (
                                              timeUntilAppointment > 0
                                            ) {
                                              // 0-15 minutes before appointment (joinable soon)
                                              return (
                                                <span className="badge badge-pill bg-info text-white border-0 mr-2">
                                                  <i className="fas fa-video mr-1"></i>
                                                  Starting soon
                                                </span>
                                              );
                                            } else if (
                                              timeSinceAppointment <= oneHour &&
                                              now < appointmentEndTime
                                            ) {
                                              // Within appointment time window (joinable)
                                              return (
                                                <button
                                                  className="btn btn-info rounded-pill "
                                                  title="Join Video Call Now"
                                                  onClick={() =>
                                                    handleJoinVideo(
                                                      item.appointmentId,
                                                      item.PatientID,
                                                    )
                                                  }
                                                  disabled={videoLoading}
                                                >
                                                  {isJoiningThisCall &&
                                                  videoLoading ? (
                                                    <>
                                                      <span
                                                        className="spinner-border spinner-border-sm mr-1"
                                                        role="status"
                                                      ></span>
                                                      Joining...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <i className="fas fa-video mr-1"></i>
                                                      Join Call
                                                    </>
                                                  )}
                                                </button>
                                              );
                                            } else if (
                                              timeSinceAppointment <=
                                              2 * 60 * 60 * 1000
                                            ) {
                                              // Within 2 hours after appointment end (recently ended)
                                              return (
                                                <span className="badge badge-pill bg-danger-light text-danger border mr-2">
                                                  <i className="fas fa-video-slash text-danger mr-1"></i>
                                                  Call Ended
                                                </span>
                                              );
                                            } else {
                                              // More than 2 hours after appointment (past)
                                              return (
                                                <span className="badge badge-pill bg-light text-muted border mr-2">
                                                  <i className="fas fa-history text-muted mr-1"></i>
                                                  Completed
                                                </span>
                                              );
                                            }
                                          })()}

                                        {/* Action Buttons */}
                                        <div className="btn-group" role="group">
                                          {/* View Details Button */}
                                          <button
                                            className="i-btn-view"
                                            onClick={() =>
                                              setViewingAppointmentDetails(item)
                                            }
                                            title="View appointment details"
                                          ></button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center py-5">
                                  <div className="empty-state">
                                    <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                                    <h5 className="text-muted">
                                      No appointments found
                                    </h5>
                                    <p className="text-muted">
                                      Try adjusting your filters or date range
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                {/* ===================================
    --- STATIC VIDEO CONSULTATION TAB PANEL ---
    (Styled exactly like the appointments tab)
    ==================================== */}
                <div
                  className={`tab-pane fade ${
                    activeTab === "videocons" ? "show active" : ""
                  }`}
                  id="pat_vidcons"
                >
                  {/* --- Static Filters --- */}
                  <div className="card-body p-0 mb-3">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>Doctor</label>
                          <select name="doctor" className="form-control">
                            <option value="">All Doctors</option>
                            <option value="101">Dr. John Doe</option>
                            <option value="102">Dr. Jane Smith</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <label>Status</label>
                          <select name="status" className="form-control">
                            <option value="">All</option>
                            <option value="Pending">Pending</option>
                            <option value="Confirm">Confirm</option>
                            <option value="Complete">Complete</option>
                            <option value="Cancel">Cancel</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>From Date</label>
                          <input
                            type="date"
                            name="startDate"
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label>To Date</label>
                          <input
                            type="date"
                            name="endDate"
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-1 d-flex align-items-end">
                        <div className="form-group">
                          <button className="btn btn-secondary">Clear</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`tab-pane fade ${
                    activeTab === "billing" ? "show active" : ""
                  }`}
                  id="pat_billing"
                >
                  <div className="card card-table mb-0">
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-hover table-center mb-0">
                          <thead>
                            <tr>
                              <th>Invoice ID</th>
                              <th>Doctor</th>
                              <th>Patient</th>
                              <th>Amount</th>
                              <th>Date</th>
                              <th className="text-center">Status</th>
                              <th className="text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {patientInvoices.length > 0 ? (
                              patientInvoices.map((invoice) => {
                                const doctor = doctorMap[invoice.hospitalID];
                                // Find the appointment associated with this invoice to get patient_name
                                const relatedAppointment = appointment.find(
                                  (appt) =>
                                    String(appt.appointmentId) ===
                                    String(invoice.appointmentId),
                                );
                                const patientName = relatedAppointment
                                  ? relatedAppointment.patient_name
                                  : "Unknown";

                                return (
                                  <tr key={invoice.id}>
                                    <td>INV-{invoice.id}</td>
                                    <td>
                                      <h2 className="table-avatar">
                                        <a className="avatar avatar-sm mr-2">
                                          <img
                                            className="avatar-img rounded-circle"
                                            src={
                                              doctor?.uploadPhoto
                                                ? `${imgUrl}${doctor.uploadPhoto}`
                                                : "/assets/img/doctors/doctor-thumb-01.jpg"
                                            }
                                            alt="Doctor"
                                          />
                                        </a>
                                        <a>{`Dr. ${doctor?.firstName} ${doctor?.lastName}`}</a>
                                      </h2>
                                    </td>
                                    <td>
                                      {(patientProfile?.firstName || "N/A") +
                                        " " +
                                        (patientProfile?.lastName || "") ===
                                      patientName
                                        ? "self"
                                        : patientName}
                                    </td>
                                    <td>{formatCurrency(invoice.TotalAmount, selectedCurrency)}</td>
                                    <td>
                                      {new Date(
                                        invoice.date,
                                      ).toLocaleDateString("en-GB")}
                                    </td>
                                    <td className="text-center">
                                      <span
                                        className={`badge badge-pill ${getInvoiceBadgeClass(
                                          invoice.Status,
                                        )}`}
                                      >
                                        {invoice.Status}
                                      </span>
                                    </td>
                                    <td className="text-right">
                                      <div className="table-action">
                                        <button
                                          className="i-btn-view"
                                          onClick={() => {
                                            setViewingInvoice(invoice);
                                            setShowInvoicePdfModal(true);
                                          }}
                                          title="View Invoice Report"
                                        ></button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan="7"
                                  className="text-center text-muted"
                                >
                                  No billing records found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- ALL MODALS --- */}
      {viewingAppointmentDetails && (
        <Modal
          show={!!viewingAppointmentDetails}
          onHide={() => setViewingAppointmentDetails(null)}
          centered
          size="xl"
          scrollable
        >
          <Modal.Header
            className="bg-info text-white"
            closeButton
            closeVariant="white"
          >
            <Modal.Title>
              Details for Appointment #{viewingAppointmentDetails.appointmentId}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row mb-4">
              <div className="col-md-6">
                <h4>Appointment Details</h4>
                <p className="mb-1">
                  <strong>Doctor:</strong> Dr.{" "}
                  {doctorMap[viewingAppointmentDetails.hospitalID]?.firstName}{" "}
                  {doctorMap[viewingAppointmentDetails.hospitalID]?.lastName}
                </p>
                <p className="mb-1">
                  <strong>Date & Time:</strong>{" "}
                  {viewingAppointmentDetails.appointmentDate} at{" "}
                  {viewingAppointmentDetails.appointmentTime}
                </p>
                <p className="mb-0">
                  <strong>Status:</strong>{" "}
                  <span
                    className={`badge badge-pill bg-${
                      viewingAppointmentDetails.status === "Pending"
                        ? "warning"
                        : "success"
                    }`}
                  >
                    {viewingAppointmentDetails.status}
                  </span>
                </p>
              </div>
              <div className="col-md-6 text-md-right">
                <h4>Patient Information</h4>
                <p className="mb-1">
                  {viewingAppointmentDetails?.patient_name}
                </p>
                <p className="mb-0">Patient ID: {patientId}</p>
              </div>
            </div>
            <hr />
            <div className="record-section">
              <h4>Prescriptions</h4>
              {viewingAppointmentDetails.prescriptions.length > 0 ? (
                <div className="table-responsive">
                  <table className="table-medical-header table table-sm table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Name</th>
                        <th>Quantity</th>
                        <th>Days</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingAppointmentDetails.prescriptions.map(
                        (pres, idx) => (
                          <tr key={`pres-${idx}`}>
                            <td>{pres.name}</td>
                            <td>{pres.quantity}</td>
                            <td>{pres.days}</td>
                            <td>
                              {Array.isArray(pres.time)
                                ? pres.time.join(", ")
                                : pres.time}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">
                  No prescriptions were issued for this appointment.
                </p>
              )}
            </div>
            <hr />
            <div className="record-section">
              <h4>Medical Records</h4>
              {viewingAppointmentDetails.medicalRecords.length > 0 ? (
                viewingAppointmentDetails.medicalRecords.map((rec, idx) => {
                  const bmiValue = calculateBmi(rec.weight, rec.height);
                  const bmiInfo = getBmiStatus(bmiValue);
                  return (
                    <div className="card mb-3" key={`rec-${idx}`}>
                      <div className="record-card-header pl-1">
                        Record from {new Date(rec.date).toLocaleDateString()}
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <strong>BP:</strong> {rec.BP || "N/A"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>Heart Rate:</strong>{" "}
                            {rec.heartRate || "N/A"} BPM
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>SPO2:</strong> {rec.SPO || "N/A"} %
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>BloodSugar:</strong> {rec.sugar || "N/A"}
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>Height/Weight:</strong>{" "}
                            {rec.height || "N/A"} cm / {rec.weight || "N/A"} kg
                          </div>
                          <div className="col-md-6 mb-2">
                            <strong>BMI:</strong> {bmiValue}{" "}
                            <span
                              className={`font-weight-bold ${bmiInfo.className}`}
                            >
                              ({bmiInfo.status})
                            </span>
                          </div>
                        </div>
                        <hr />
                        <h5>Diagnosis :</h5>
                        <p>{rec.diagnosis || "N/A"}</p>
                        <h5>Treatment :</h5>
                        <p>{rec.treatment || "N/A"}</p>
                        <div>
                          <h5>Next follow_up_date: </h5>
                          <p>{rec.follow_up_date || "N/A"}</p>
                        </div>

                        {rec.uploadFile && (
                          <div>
                            <a
                              href={`${imgUrl}${rec.uploadFile}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Attachment
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-muted">
                  No medical records were created for this appointment.
                </p>
              )}
            </div>
            {/* --- 2. DIET PLANS SECTION --- */}
<hr />

<div className="record-section">
  <h4 className="text-warning"><i className="fas fa-utensils mr-2"></i>Nutrition / Diet Plans</h4>
  {viewingAppointmentDetails.diets && viewingAppointmentDetails.diets.length > 0 ? (
    viewingAppointmentDetails.diets.map((asgn, idx) => (
      <div key={`asgn-${idx}`}>
        {/* Yahan assigned_diets array par map chalayein */}
        {asgn.assigned_diets?.map((plan, pIdx) => (
          <div className="card mb-3 border-warning shadow-sm" key={`modal-diet-${idx}-${pIdx}`}>
            <div className="card-header bg-warning-light d-flex justify-content-between align-items-center">
              <span className="font-weight-bold text-dark">{plan.title} <small className="text-muted">({plan.diet_type})</small></span>
              <span className="badge badge-warning text-white">{asgn.start_date} to {asgn.end_date || 'Further notice'}</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th width="30%">Meal Time</th>
                      <th>Food Items & Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.meals?.map((meal, mIdx) => (
                      <tr key={mIdx}>
                        <td className="font-weight-bold text-warning">{meal.meal_type}</td>
                        <td>
                          <div className="text-dark fw-bold">{meal.food_items}</div>
                          <small className="text-muted italic">{meal.instructions}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    ))
  ) : (
    <p className="text-muted italic small p-3 bg-light rounded text-center">No diet plans assigned for this appointment.</p>
  )}
</div>
           {/* --- 1. EXERCISE PLANS SECTION --- */}
<hr />
<div className="record-section">
  <h4 className="text-primary"><i className="fas fa-running mr-2"></i>Exercise Plans</h4>
  {viewingAppointmentDetails.exercises && viewingAppointmentDetails.exercises.length > 0 ? (
    viewingAppointmentDetails.exercises.map((asgn, idx) => (
      <div className="card mb-4 border-info shadow-sm" key={`modal-ex-${idx}`}>
        <div className="card-header bg-primary-light d-flex justify-content-between align-items-center">
          <span className="font-weight-bold text-primary">Plan #{asgn.id}</span>
          <span className="badge badge-pill badge-primary">{asgn.frequency} ({asgn.time_of_day})</span>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-between small text-muted mb-3 border-bottom pb-2">
            <span><i className="far fa-calendar-alt mr-1"></i> {asgn.start_date} to {asgn.end_date || 'Ongoing'}</span>
            <span><i className="far fa-clock mr-1"></i> Assigned: {asgn.assigned_date}</span>
          </div>

          {asgn.assigned_exercises?.map((ex, eIdx) => (
            <div key={eIdx} className="exercise-item mb-4 last-child-mb-0">
              <h6 className="font-weight-bold text-dark mb-1">
                {ex.exercise_title} <span className="text-muted small">({ex.exercise_type})</span>
              </h6>
              <p className="small text-secondary italic mb-3">{ex.description}</p>

              {/* Steps Layout starts here */}
              <div className="steps-list">
                {ex.steps?.map((step, sIdx) => (
                  <div className="row no-gutters mb-3 align-items-center p-2 border rounded bg-white" key={sIdx}>
                    
                    {/* 40% Image Column */}
                    <div className="col-5 col-md-4 pr-3 text-center border-right">
                      {step.image && step.image !== "null" ? (
                        <img
                          src={`${imgUrl}${step.image}`}
                          alt={step.step_title}
                          className="img-fluid rounded"
                          style={{ maxHeight: '100px', objectFit: 'contain' }}
                        />
                      ) : (
                        <div className="bg-light d-flex align-items-center justify-content-center rounded" style={{ height: '80px' }}>
                          <i className="fas fa-image text-muted fa-2x"></i>
                          <small className="d-block text-muted">No Image</small>
                        </div>
                      )}
                    </div>

                    {/* 60% Content Column */}
                    <div className="col-7 col-md-8 pl-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="badge badge-info mb-1" style={{fontSize: '10px'}}>Step {step.step_number}</span>
                        <span className="small text-info font-weight-bold">
                          <i className="far fa-clock mr-1"></i>{step.duration_seconds}s
                        </span>
                      </div>
                      <div className="font-weight-bold text-dark mb-1" style={{ fontSize: '0.9rem' }}>
                        {step.step_title}
                      </div>
                      <div className="small text-muted text-truncate-custom">
                        {step.description}
                      </div>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    ))
  ) : (
    <p className="text-muted italic small">No exercise plans assigned for this appointment.</p>
  )}
</div>

{/* CSS for better handling long text in Modal */}
<style>{`
  .bg-primary-light { background-color: #eef7ff; }
  .text-truncate-custom {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.3;
  }
  .last-child-mb-0:last-child { margin-bottom: 0 !important; }
`}</style>


          </Modal.Body>
          <Modal.Footer>
            {/* <Button
              variant="light"
              onClick={() => setViewingAppointmentDetails(null)}
            >
              Close
            </Button> */}
            <Button
              variant="info rounded-pill"
              onClick={() => setShowPrescriptionPdfModal(true)}
              disabled={viewingAppointmentDetails.prescriptions.length === 0}
            >
              <i className="fas fa-print mr-1"></i> Prescriptions
            </Button>
            <Button
              variant="info rounded-pill"
              onClick={() => setShowMedicalRecordPdfModal(true)}
              disabled={viewingAppointmentDetails.medicalRecords.length === 0}
            >
              <i className="fas fa-print mr-1"></i> Records
            </Button>
             <Button variant="primary rounded-pill" onClick={() => setShowExercisePdfModal(true)} disabled={viewingAppointmentDetails.exercises?.length === 0}>
    <i className="fas fa-running mr-1"></i> Exercise Chart
  </Button>
  <Button variant="warning rounded-pill" className="text-white" onClick={() => setShowDietPdfModal(true)} disabled={viewingAppointmentDetails.diets?.length === 0}>
    <i className="fas fa-utensils mr-1"></i> Diet Plan
  </Button>
          </Modal.Footer>
        </Modal>
      )}

      {showPrescriptionPdfModal && viewingAppointmentDetails && (
        <Modal
          show={showPrescriptionPdfModal}
          onHide={() => setShowPrescriptionPdfModal(false)}
          size="xl"
          centered
        >
          <Modal.Header className="bg-info" closeButton closeVariant="white">
            <Modal.Title>Prescription Report</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: "80vh" }}>
            <PDFViewer width="100%" height="100%">
              <PrescriptionPDF
                prescriptions={viewingAppointmentDetails.prescriptions}
                patient={patientProfile}
                doctor={doctorMap[viewingAppointmentDetails.hospitalID]}
                appointmentId={viewingAppointmentDetails.appointmentId}
              />
            </PDFViewer>
          </Modal.Body>
        </Modal>
      )}
{/* --- EXERCISE PDF VIEWER --- */}
{showExercisePdfModal && viewingAppointmentDetails && (
  <Modal show={showExercisePdfModal} onHide={() => setShowExercisePdfModal(false)} size="xl" centered>
    <Modal.Header className="bg-primary text-white" closeButton closeVariant="white">
      <Modal.Title>Exercise Chart Report</Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ height: "80vh" }}>
      <PDFViewer width="100%" height="100%">
        <ExerciseChartPDF
          exercises={viewingAppointmentDetails.exercises}
          patient={patientProfile}
          doctor={doctorMap[viewingAppointmentDetails.hospitalID]}
          appointmentId={viewingAppointmentDetails.appointmentId}
        />
      </PDFViewer>
    </Modal.Body>
  </Modal>
)}

{/* --- DIET PDF VIEWER --- */}
{showDietPdfModal && viewingAppointmentDetails && (
  <Modal show={showDietPdfModal} onHide={() => setShowDietPdfModal(false)} size="xl" centered>
    <Modal.Header className="bg-warning text-white" closeButton closeVariant="white">
      <Modal.Title>Nutrition Plan Report</Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ height: "80vh" }}>
      <PDFViewer width="100%" height="100%">
        <DietChartPDF
          diets={viewingAppointmentDetails.diets}
          patient={patientProfile}
          doctor={doctorMap[viewingAppointmentDetails.hospitalID]}
          appointmentId={viewingAppointmentDetails.appointmentId}
        />
      </PDFViewer>
    </Modal.Body>
  </Modal>
)}
      {showMedicalRecordPdfModal && viewingAppointmentDetails && (
        <Modal
          show={showMedicalRecordPdfModal}
          onHide={() => setShowMedicalRecordPdfModal(false)}
          size="xl"
          centered
        >
          <Modal.Header className="bg-info " closeButton closeVariant="white">
            <Modal.Title>Medical Records Report</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: "80vh" }}>
            <PDFViewer width="100%" height="100%">
              <MedicalRecordPDF
                records={viewingAppointmentDetails.medicalRecords}
                patient={patientProfile}
                doctor={doctorMap[viewingAppointmentDetails.hospitalID]}
              />
            </PDFViewer>
          </Modal.Body>
        </Modal>
      )}

      {showInvoicePdfModal && viewingInvoice && (
        <Modal
          show={showInvoicePdfModal}
          onHide={() => setShowInvoicePdfModal(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Invoice Report</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: "80vh" }}>
            <PDFViewer width="100%" height="100%">
              <InvoicePDF
                invoice={viewingInvoice}
                patient={patientProfile}
                doctor={doctorMap[viewingInvoice.hospitalID]}
                selectedCurrency={selectedCurrency} 
              />
            </PDFViewer>
          </Modal.Body>
        </Modal>
      )}

      <MessageModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        title={modalTitle}
        message={modalMessage}
        variant={modalVariant}
      />
      <style>{`
  .bg-warning-light { background-color: #fff9e6; }
  .border-warning { border-color: #ffc107 !important; }
  .border-info { border-color: #17a2b8 !important; }
  .record-section h4 { 
    font-size: 1.1rem; 
    border-bottom: 2px solid #eee; 
    padding-bottom: 8px; 
    margin-top: 15px;
  }
`}</style>
    </>
  );
};

const mapStateToProps = (state) => ({
   assignmentList: state.exerciseAssignment?.assignmentList || [],
  patientDietList: state.patientDiet?.patientDietList || [],
  appointment: state.appointment?.all || [],
  appointmentLoading: state.appointment?.loading,
  doctors: state.doctorList?.doctors || [],
  doctorsLoading: state.doctorList?.loading,
  prescription: state.prescription?.prescriptions || [],
  prescriptionLoading: state.prescription?.loading,
  medicalRecords: state.medicalRecord?.records || [],
  medicalRecordsLoading: state.medicalRecord?.loading,
  invoices: state.invoice?.invoices || [],
  invoicesLoading: state.invoice?.loading,
  patientProfile: state.patientProfile.profile,
   selectedCurrency: state.currencyReducer.selectedCurrency,
  video: state.video, // <-- ADD THIS LINE to get the video slice from the store
});

// Find your connect call
const mapDispatchToProps = {
  getAllAppointments,
  getAllDoctors,
  getAllPrescriptions,
  getAllMedicalRecords,
  listAllInvoices,
  getOnePatientProfile,
  patientGenerateToken,
  clearVideoState,
   getExerciseAssignments,
  getPatientDiet,
};

// Find your export statement:
// export default connect(mapStateToProps, mapDispatchToProps)(PatientDashboardMain);

// REPLACE it with this:
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PatientDashboardMain),
);
